import React, { useMemo } from "react";
import ReactECharts from "echarts-for-react";
import {
  getMaxDay,
  getBreedIdealSeries,
} from "../comparison/growthComparisonUtils";
import i18n from '../../i18n';
// Chart option objects are built in plain helper functions and default
// parameters, where React hooks cannot be called — so translate through the
// i18n instance directly. Charts re-render when their page re-renders on a
// language change, so labels stay in sync.
const t = (key, opts) => i18n.t(key, opts);

// Stable palette so a batch keeps its color across re-renders (up to 10 batches)
const PALETTE = [
  "#5470c6",
  "#91cc75",
  "#ee6666",
  "#fac858",
  "#73c0de",
  "#3ba272",
  "#fc8452",
  "#9a60b4",
  "#ea7ccc",
  "#2f4554",
];

// Muted colors for the dashed ideal curves (one per distinct breed)
const IDEAL_PALETTE = ["#8a8a8a", "#b58900", "#7f5f9e", "#5f7a61"];

const GrowthComparisonChart = ({ data, title }) => {
  const processed = useMemo(() => {
    if (!data || data.length === 0) return null;

    const maxDay = getMaxDay(data);
    const xAxisData = Array.from({ length: maxDay + 1 }, (_, i) => i);

    const multiFarm =
      new Set(data.map((batch) => batch.farmName)).size > 1;

    const series = data.map((batch) => {
      // Pad each batch onto the common day axis; days past its close stay null
      const values = new Array(maxDay + 1).fill(null);
      (batch.days || []).forEach((day, idx) => {
        if (day >= 0 && day <= maxDay) values[day] = batch.weights[idx];
      });
      return {
        name:
          multiFarm && batch.farmName
            ? `${batch.farmName} — ${batch.batchName}`
            : batch.batchName,
        type: "line",
        smooth: true,
        symbol: "circle",
        symbolSize: 4,
        connectNulls: true,
        emphasis: { focus: "series" },
        data: values,
      };
    });

    const breedIdeals = getBreedIdealSeries(data);
    const singleBreed = breedIdeals.size === 1;
    let idealIndex = 0;
    breedIdeals.forEach(({ batch }, breed) => {
      const values = new Array(maxDay + 1).fill(null);
      (batch.days || []).forEach((day, idx) => {
        if (day >= 0 && day <= maxDay) values[day] = batch.idealWeights[idx];
      });
      const color = IDEAL_PALETTE[idealIndex % IDEAL_PALETTE.length];
      idealIndex += 1;
      series.push({
        name: singleBreed ? `Ideal Weight (${breed})` : `Ideal — ${breed}`,
        type: "line",
        smooth: true,
        symbol: "none",
        connectNulls: true,
        lineStyle: { type: "dashed", width: 2, color },
        itemStyle: { color },
        emphasis: { focus: "series" },
        data: values,
      });
    });

    return { xAxisData, series };
  }, [data]);

  const option = useMemo(() => {
    if (!processed) return null;
    return {
      title: {
        text: title,
        left: "center",
        top: 10,
        textStyle: { fontSize: 16, fontWeight: "bold", color: "#333" },
      },
      color: PALETTE,
      tooltip: {
        trigger: "axis",
        confine: true,
        borderWidth: 0,
        backgroundColor: "rgba(30,30,30,0.9)",
        textStyle: { color: "#fff" },
        valueFormatter: (value) =>
          value != null ? `${Number(value).toFixed(2)} kg` : "-",
      },
      legend: {
        type: "scroll",
        top: 40,
        data: processed.series.map((s) => s.name),
      },
      grid: { left: 50, right: 40, top: 80, bottom: 80, containLabel: true },
      xAxis: {
        type: "category",
        boundaryGap: false,
        data: processed.xAxisData,
        name: t('charts:axis.ageDays'),
        nameLocation: "middle",
        nameGap: 30,
        nameTextStyle: { fontSize: 13, fontWeight: "bold", color: "#666" },
      },
      yAxis: {
        type: "value",
        name: t('charts:axis.avgBirdWeightKg'),
        nameTextStyle: { fontSize: 13, fontWeight: "bold", color: "#666" },
        axisLabel: { color: "#666" },
        splitLine: { lineStyle: { type: "dashed", color: "#e0e0e0" } },
      },
      dataZoom: [
        { type: "slider", bottom: 15 },
        { type: "inside" },
      ],
      series: processed.series,
    };
  }, [processed, title]);

  if (!option) {
    return (
      <div style={{ textAlign: "center", padding: "40px", color: "#6c757d" }}>
        {t('charts:common.noDataFound')}
      </div>
    );
  }

  return (
    <ReactECharts
      option={option}
      style={{ height: "500px", width: "100%" }}
      opts={{ renderer: "svg" }}
      notMerge={true}
    />
  );
};

export default GrowthComparisonChart;
